/**
 * 测试环境快速填充工具
 * 用于开发测试时快速生成答案
 */

import { animalArchetypes } from '@/data/animalArchetypes'
import { animalScoreMap, dimensionKeys } from '@/data/animalQuestions'
import { normalizeByMax, getMaxPerDimension, l2Normalize, cosineSimilarity } from '@/utils/animalCalculator'

/**
 * 根据动物类型生成匹配的答案
 * 使用贪心算法：对每个问题，选择能最大化与目标动物向量相似度的选项
 * @param {string} animalName - 动物名称
 * @returns {Object} 答案对象 { "1": "A", "2": "B", ... }
 */
export function generateAnswersForAnimal(animalName) {
  const animal = animalArchetypes[animalName]
  if (!animal) {
    throw new Error(`未找到动物类型: ${animalName}`)
  }

  const targetVector = animal.vector
  const targetVectorNormalized = l2Normalize(targetVector)
  const maxPerDim = getMaxPerDimension()
  
  const answers = {}
  const currentScores = {}
  
  // 初始化当前分数
  dimensionKeys.forEach(k => {
    currentScores[k] = 0
  })

  // 对每个问题，选择最佳选项
  for (let i = 0; i < animalScoreMap.length; i++) {
    const questionIndex = i + 1
    const scoreMap = animalScoreMap[i]
    
    let bestOption = 'A'
    let bestSimilarity = -Infinity
    
    // 尝试每个选项
    for (const option of ['A', 'B', 'C', 'D']) {
      const optionScores = scoreMap[option]
      if (!optionScores) continue
      
      // 临时计算选择该选项后的分数
      const tempScores = { ...currentScores }
      dimensionKeys.forEach(dim => {
        tempScores[dim] += (optionScores[dim] || 0)
      })
      
      // 归一化
      const normalized = normalizeByMax(tempScores, maxPerDim)
      const normalizedL2 = l2Normalize(normalized)
      
      // 计算相似度
      const similarity = cosineSimilarity(normalizedL2, targetVectorNormalized)
      
      if (similarity > bestSimilarity) {
        bestSimilarity = similarity
        bestOption = option
      }
    }
    
    // 选择最佳选项并更新当前分数
    answers[questionIndex] = bestOption
    const bestOptionScores = scoreMap[bestOption]
    dimensionKeys.forEach(dim => {
      currentScores[dim] += (bestOptionScores[dim] || 0)
    })
  }
  
  return answers
}

/**
 * 快速填充所有答案为指定选项
 * @param {string} option - 选项 ('A', 'B', 'C', 'D')
 * @returns {Object} 答案对象
 */
export function generateAnswersForOption(option) {
  if (!['A', 'B', 'C', 'D'].includes(option)) {
    throw new Error(`无效的选项: ${option}`)
  }
  
  const answers = {}
  for (let i = 1; i <= 60; i++) {
    answers[i] = option
  }
  
  return answers
}

